import '../SettingsPage.scss';
import { Button, Checkbox, Dropdown, Icon, Input } from '@design-system/index';
import { ArchiveFilled, ArchiveLine, ModifierLine, ModifierFull, UnarchiveLine, UnarchiveFilled, Right, Wrong } from '@icons';
import { MutationTable } from '@components/Table/MutationTable/MutationTable';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { useETicketsPageVM } from './useETicketsPageVM';
import { Formik } from 'formik';
import { ETicketToCreate, ETicket } from '@services/product/eTicket/e-ticket.types';
import { formatDate } from '@utils/dates';

export default function ETicketsPage() {
    const {
        mutationHook,
        isArchived,
        handleToggleArchiveETickets,
        handleToggleArchiveETicket,
        updateETicket,
        createETicket,
        MySwal,
        eticketTypes,
        needReload,
        setIsArchived
    } = useETicketsPageVM();
    const columnHelper = createColumnHelper<ETicket>();

    const handleModificationETickets = async (eticket: ETicketToCreate | null, id?: number) => {
        await MySwal.fire<ETicketToCreate>({
            title: eticket ? "Modification d'un e-billet" : "Création d'un e-billet",
            showCancelButton: true,
            confirmButtonColor: '#5D4B99',
            confirmButtonText: eticket ? 'Modifier' : 'Créer',
            cancelButtonColor: '#FF5F46',
            cancelButtonText: 'Annuler',
            html: (
                <Formik
                    initialValues={{ label: eticket?.label ?? '', code: eticket?.code ?? '', pin: eticket?.pin, expireAt: eticket?.expireAt, eticketType: eticket?.eticketType ?? undefined }}
                    onSubmit={(values) => {
                        if (id) {
                            updateETicket({
                                id: id.toString(),
                                eticket: {
                                    label: values.label,
                                    code: values.code,
                                    pin: values.pin,
                                    expireAt: values.expireAt,
                                    eticketType: values.eticketType
                                }
                            });
                        } else {
                            createETicket({
                                label: values.label,
                                code: values.code,
                                pin: values.pin,
                                expireAt: values.expireAt,
                                eticketType: values.eticketType
                            });
                        }
                        MySwal.close();
                    }}
                >
                    {({ values, handleChange, handleSubmit, setFieldValue }) => (
                        <form id="form-eticket" onSubmit={handleSubmit}>
                            <Dropdown
                                name="eticketType"
                                label="Type de E-billet"
                                value={values.eticketType}
                                required
                                onOptionChange={(option) => {
                                    setFieldValue('eticketType', option);
                                }}
                                options={eticketTypes.map((eticketType) => ({
                                    label: eticketType.label,
                                    id: eticketType.id
                                }))}
                            />
                            <Input name="label" label="Libellé" placeholder="Libellé" value={values.label} required onChange={handleChange} />
                            <Input name="code" label="Code" placeholder="Code" value={values.code} required onChange={handleChange} />
                            <Input name="pin" label="Pin" placeholder="Pin" value={values.pin} onChange={handleChange} />
                            <Input name="expireAt" type="date" label="Expire le" placeholder="Expire le" value={values.expireAt} onChange={handleChange} />
                        </form>
                    )}
                </Formik>
            ),
            focusConfirm: false,
            preConfirm: () => {
                const form = document.getElementById('form-eticket') as HTMLFormElement;
                form.requestSubmit();
                return false;
            }
        });
    };

    const columns = [
        columnHelper.display({
            id: 'select',
            size: 0,
            cell: ({ row }) => (
                <Checkbox
                    checked={row.getIsSelected()}
                    onChange={row.getToggleSelectedHandler()}
                    aria-label={`Sélectionner ligne ${row.id}`}
                />
            )
        }),
        columnHelper.accessor('eticketType.label', {
            id: 'eticketType.label',
            header: 'Type',
            enableSorting: true,
            cell: (info) => {
                return <>{info.getValue()}</>;
            }
        }),
        columnHelper.accessor('label', {
            header: 'Libellé',
            enableSorting: true,
            cell: (info) => {
                return <>{info.getValue()}</>;
            }
        }),
        columnHelper.accessor('code', {
            header: 'Code',
            enableSorting: true,
            cell: (info) => {
                return <>{info.getValue()}</>;
            }
        }),
        columnHelper.accessor('pin', {
            header: 'Pin',
            enableSorting: true,
            cell: (info) => {
                return <>{info.getValue()}</>;
            }
        }),
        columnHelper.accessor('expireAt', {
            header: 'Expire le',
            enableSorting: true,
            cell: (info) => {
                return <>{info.getValue() ? formatDate(info.getValue()) : ''}</>;
            }
        }),
        columnHelper.accessor('isAvailable', {
            header: 'Disponible',
            enableSorting: true,
            meta: {
                style: {
                    textAlign: 'center'
                }
            },
            cell: (info) => {
                return <div style={{display: 'flex', justifyContent: 'center'}}>
                    {info.getValue() ? <Icon className="available-icon-force-color" icon={<Right />} color="green" size={20} /> : <Icon icon={<Wrong />} color="error" size={20} />}
                </div>;
            }
        }),
        columnHelper.display({
            header: 'Actions',
            cell: (info) => {
                return (
                    <div className="settings-actions">
                        <Button
                            variant="icon"
                            color="primary"
                            size="small"
                            hoverIcon={<Icon icon={<ModifierFull />} size={20} color="primary" />}
                            icon={<Icon icon={<ModifierLine />} size={20} color="primary" />}
                            onClick={() => {
                                handleModificationETickets({label: info.row.original.label, code: info.row.original.code, pin: info.row.original.pin, expireAt: info.row.original.expireAt, eticketType: info.row.original.eticketType}, info.row.original.id);
                            }}
                        />
                        <Button
                            variant="icon"
                            color="primary"
                            size="small"
                            hoverIcon={<Icon icon={isArchived ? <UnarchiveFilled /> : <ArchiveFilled />} size={20} color="primary" />}
                            icon={<Icon icon={isArchived ? <UnarchiveLine /> : <ArchiveLine />} size={20} color="primary" />}
                            onClick={() => handleToggleArchiveETicket(info.row.original.id, !isArchived)}
                        />
                    </div>
                );
            }
        })
    ] as ColumnDef<ETicket>[];

    return (
        <>
            <div className="settings-tabs-content etickets">
                <MutationTable
                    noHeader
                    key={isArchived ? 'archived' : 'active'}
                    columns={columns}
                    mutationHook={mutationHook}
                    defaultSorting={{
                        field: 'label',
                        direction: 'desc'
                    }}
                    needReload={needReload}
                    createButtonOnClick={() => handleModificationETickets(null)}
                    createButtonLabel="Créer un e-billet"
                    archiveButtonOnClick={() => setIsArchived(!isArchived)}
                    archiveButtonLabel={isArchived ? 'Actifs' : 'Archivés'}
                    deleting={{
                        enabled: isArchived,
                        entity: 'eticket',
                    }}
                    filters={[
                        {
                            id: "isAvailable",
                            label: "Disponible",
                            type: "boolean"
                        },
                    ]}
                    footer={{
                        actions: [
                            {
                                label: isArchived ? 'Désarchiver' : "Archiver",
                                icon: <Icon icon={isArchived ? <UnarchiveLine /> : <ArchiveLine />} size={20} color="primary" />,
                                hoverIcon: <Icon icon={isArchived ? <UnarchiveFilled /> : <ArchiveFilled />} size={20} color="primary" />,
                                onClick: (lines: ETicket[]) => handleToggleArchiveETickets(lines, !isArchived)
                            }
                        ]
                    }}
                />
            </div>
        </>
    );
}
